import { pluralize } from '../utils'
import { formatCount } from '../Results/Run/ccqRunCopy'

export const SEND_BOOKING_INVITATIONS_BACK = 'Back to results'

export const SEND_BOOKING_INVITATIONS_TITLE = 'Send booking invitations'

export const sendBookingInvitationsSubtitle = (count: number, question: string): string =>
  `${formatCount(count)} ${pluralize(count, 'client')} selected from the query "${question}"`

export const SEND_BOOKING_INVITATIONS_CLIENTS_ERROR = 'Failed to load the selected clients. Please try again.'

export const SEND_BOOKING_INVITATIONS_NO_CLIENTS = 'None of the selected clients are in Matched anymore. Go back to the results to choose again.'

export const MEETING_OWNER_TITLE = 'Meeting owner'

export const MEETING_OWNER_DESCRIPTION = 'Whose calendar the meeting goes on. The invitation goes out in their name.'

export const MEETING_OWNER_EACH = "Each client's owner"

export const MEETING_OWNER_SINGLE = 'One user for all clients'

export const MEETING_OWNER_USER = 'Owner'

const clientsHave = (count: number): string => (count === 1 ? '1 client has' : `${formatCount(count)} clients have`)

export const noOwnerSentence = (count: number): string => `${clientsHave(count)} no owner.`

export const ownerNotSharedSentence = (count: number): string =>
  count === 1 ? "1 client's owner hasn't shared scheduling with you." : `${formatCount(count)} clients' owners haven't shared scheduling with you.`

export const cantSendFromOwnerSentence = (count: number): string => `${formatCount(count)} ${pluralize(count, 'client')} can't be sent from their owner.`

export const skippedBreakdownSentence = (noOwnerCount: number, notSharedCount: number): string =>
  `${formatCount(noOwnerCount)} ${noOwnerCount === 1 ? 'has' : 'have'} no owner and ${formatCount(notSharedCount)} ${
    notSharedCount === 1 ? 'has' : 'have'
  } an owner who hasn't shared scheduling with you.`

export const sentFromInsteadSentence = (count: number, name: string): string =>
  `${formatCount(count)} ${pluralize(count, 'client')} will be sent from ${name} instead of their owner.`

export const PICK_BACKUP_OWNER = 'Pick a backup owner to include them.'

export const NO_OWNER_SEE_WHO = 'See who'

export const SKIPPED_GROUP_NO_OWNER = 'No owner'

export const SKIPPED_GROUP_OWNER_NOT_SHARED = "Owner hasn't shared scheduling"

export const skippedGroupOwnerOff = (eventTypeName: string): string => `Owner hasn't turned on ${eventTypeName}`

export const skippedShowAll = (count: number): string => `Show all ${formatCount(count)}`

export const SKIPPED_OPEN_IN_RESULTS = 'Open in results'

export const NO_OWNER_YOU = 'you'

export const NO_OWNER_SELECT_LABEL = 'Backup owner'

export const noOwnerSkip = (count: number): string => (count === 1 ? 'Skip this client' : 'Skip these clients')

export const sendFromOption = (name: string): string => `Send from ${name}`

export const MEETING_OWNER_SELECT_USER = 'Select a user'

export const MEETING_OWNER_LOAD_ERROR = 'Failed to load the client owners. Please try again.'

export const EVENT_TYPE_TITLE = 'Event type'

export const EVENT_TYPE_DESCRIPTION = 'Sets meeting length and place. Clients pick any open time on the booking page.'

export const EVENT_TYPE_LOAD_ERROR = 'Failed to load the event types. Please try again.'

export const EVENT_TYPE_HOSTS_ERROR = "Couldn't check who has turned this on. Please try again."

export const eventTypeSkipped = (skippedCount: number): string => `${formatCount(skippedCount)} ${pluralize(skippedCount, 'client')} will be skipped.`

export const EVENT_TYPE_OWNER_YOU = 'you'

export const eventTypeNotTurnedOn = (ownerNames: string[]): string => {
  if (ownerNames.length > 2) {
    return `${formatCount(ownerNames.length)} owners haven't turned this on.`
  }
  const subject = ownerNames.join(' and ')
  const verb = ownerNames.length === 1 && ownerNames[0] !== EVENT_TYPE_OWNER_YOU ? "hasn't" : "haven't"
  return `${subject.charAt(0).toUpperCase()}${subject.slice(1)} ${verb} turned this on.`
}

export const UNKNOWN_USER = 'Unknown user'

export const PREVIEW_TITLE = 'Email preview'

export const PREVIEW_PICK_EVENT_TYPE = 'Pick an event type to see the invitation.'

export const PREVIEW_TEMPLATES_ERROR = 'Your email templates could not be loaded. Please try again.'

export const PREVIEW_NO_TEMPLATE = 'No email template is set up for booking invitations yet. Configure one in Setup.'

export const PREVIEW_LOAD_ERROR = 'Who gets an invitation could not be loaded. Please try again.'

export const PREVIEW_PREVIOUS = 'Previous client'

export const PREVIEW_NEXT = 'Next client'

export const previewPosition = (name: string, index: number, total: number): string => `${name} · ${formatCount(index + 1)} of ${formatCount(total)}`

export const previewNoEmail = (firstName: string): string => `Won't be sent. ${firstName} has no email address.`

export const previewUnsubscribed = (firstName: string): string => `Won't be sent. ${firstName} has unsubscribed from booking invitations.`

export const previewNoOwner = (firstName: string): string => `Won't be sent. ${firstName} has no owner.`

export const previewOwnerNotShared = (firstName: string): string => `Won't be sent. ${firstName}'s owner hasn't shared their scheduling with you.`

export const previewOwnerOff = (firstName: string, eventTypeName: string): string => `Won't be sent. ${firstName}'s owner hasn't turned on ${eventTypeName}.`

export const sendClientCount = (count: number): string => `${formatCount(count)} ${pluralize(count, 'client')}`

export const SEND_INVITED = 'will get an invitation'

export const SEND_SKIPPED = 'will be skipped'

export const sendNoEmail = (count: number): string => `${formatCount(count)} ${count === 1 ? 'has' : 'have'} no email address`

export const sendUnsubscribed = (count: number): string => `${formatCount(count)} ${count === 1 ? 'has' : 'have'} unsubscribed from booking invitations`

export const sendNoOwner = (count: number): string => `${formatCount(count)} ${count === 1 ? 'has' : 'have'} no owner`

export const sendOwnerNotShared = (count: number): string =>
  `${formatCount(count)} ${count === 1 ? "has an owner who hasn't" : "have owners who haven't"} shared their scheduling with you`

export const sendOwnerWithout = (count: number): string => `${formatCount(count)} ${count === 1 ? 'has' : 'have'} an owner without`

export const SEND_OWNER_TURNED_ON = 'turned on'

export const sendNotFound = (count: number): string => `${formatCount(count)} could not be found`

export const sendButtonLabel = (count: number | null): string =>
  count === null ? 'Send invitations' : `Send ${formatCount(count)} ${pluralize(count, 'invitation')}`

export const sendSucceededToast = (count: number): { title: string; messages: string[] } => ({
  title: `Sending ${formatCount(count)} booking ${pluralize(count, 'invitation')}`,
  messages: ['Each client gets an email with a link to book.'],
})

export const SEND_FAILED_TITLE = "Couldn't send the invitations"

export const SEND_FAILED_MESSAGE = 'Please try again.'

export const SEND_LOAD_ERROR = 'Who gets an invitation could not be loaded.'

export const SEND_PICK_EVENT_TYPE = 'Pick an event type to see who gets an invitation.'

export const sendSkippedReasons = (count: number): string => (count === 1 ? 'Why 1 client is skipped' : `Why ${formatCount(count)} clients are skipped`)

export const LOCATION_TITLE = 'Location'

export const LOCATION_DESCRIPTION = 'Use the account default or set a custom location.'

export const LOCATION_FIXED_NOTE = "Added to the calendar event now. It won't update later."
